import React from 'react';
import LoginModal from '../Login/LoginModal';
import ErrorModal from '../common/ErrorModal';
import ShareTextModal from '../common/ShareTextModal';
import { API_BASE_URL } from '../../lib/constants';
import { generateDownloadFilename } from '../../utils/fileUtils';
import { handleUnifiedError } from '../../utils/unifiedErrorHandler';
import SizeSelector from '../AiToolTemplate/components/SizeSelector/SizeSelector';

const quickStyles = ['Enhance Details', 'Art Style', 'Anime Style', 'Realistic Photo'];

const ImageEditingPanelView = ({
  t,
  uploadedImages,
  isDragOver,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleFileSelect,
  removeImage,
  selectedStyle,
  setSelectedStyle,
  prompt,
  setPrompt,
  selectedSize,
  setSelectedSize,
  generationCount,
  creditsPerImage,
  isGenerating,
  generationProgress,
  handleGenerateImage,
  generatedImage,
  shareMediaUrl,
  shareOpen,
  setShareOpen,
  showLoginModal,
  handleCloseLogin,
  handleLoginSuccess,
  showUploadErrorModal,
  setShowUploadErrorModal
}) => {
  const handleStyleClick = (style) => {
    if (selectedStyle === style) {
      setSelectedStyle('');
    } else {
      setSelectedStyle(style);
    }
  };

  const completedImageUrl = () => `${API_BASE_URL}${generatedImage.url}`

  const handleImageClick = () => {
    if (!generatedImage || !generatedImage.url) return;
    const url = generatedImage.url.startsWith('http')
      ? generatedImage.url
      : completedImageUrl();
    if (url) window.open(url, '_blank');
  };

  const handleDownload = () => {
    if (!generatedImage || !generatedImage.url) return;
    const downloadUrl = generatedImage.url.startsWith('http')
      ? generatedImage.url
      : completedImageUrl();

    fetch(downloadUrl)
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const filename = generateDownloadFilename(generatedImage.id);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${filename}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        handleUnifiedError(error, t, {
          showToast: true,
          fallbackMessage: t('aiWorkbench.downloadImageError')
        });
      });
  };

  return (
    <div className="image-editing-panel">
      <div className="upload-panel">
        <div
          className={`upload-box ${isDragOver ? 'drag-over' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-input').click()}
        >
          <input
            id="file-input"
            type="file"
            multiple
            accept="image/png,image/jpg,image/jpeg,image/webp"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <p>{t('aiWorkbench.dragOrClickUpload')}</p>
          <span>{t('aiWorkbench.supportedFormats')}</span>
          <span>
            {t('aiWorkbench.maxFileSize', {
              remaining: 5 - uploadedImages.length
            })}
          </span>
        </div>
        {uploadedImages.length > 0 && (
          <div className="uploaded-images">
            <h4>
              {t('aiWorkbench.uploadedImages', {
                count: uploadedImages.length
              })}
            </h4>
            <div className="images-grid">
              {uploadedImages.map((image) => (
                <div key={image.id} className="image-item">
                  <img src={image.preview} alt={image.name} />
                  <button
                    className="remove-btn"
                    onClick={() => removeImage(image.id)}
                  >
                    ×
                  </button>
                  <span className="image-name">{image.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="conversational-editing">
          <h4>{t('aiWorkbench.conversationalEditing')}</h4>
          <p>{t('aiWorkbench.conversationalEditingDescription')}</p>
          <ul>
            <li>{t('aiWorkbench.conversationalEditingFeature')}</li>
            <li>{t('aiWorkbench.multiImageReference')}</li>
            <li>{t('aiWorkbench.intelligentUnderstanding')}</li>
          </ul>
        </div>
      </div>
      <div className="editing-controls-panel">
        <h3>{t('aiWorkbench.aiImageEditing')}</h3>
        <div className="quick-styles">
          <h4>{t('aiWorkbench.quickStyles')}</h4>
          <div className="styles-container">
            {quickStyles.map((style) => (
              <button
                key={style}
                className={`style-btn ${
                  selectedStyle === style ? 'active' : ''
                }`}
                onClick={() => handleStyleClick(style)}
              >
                {t(
                  `aiWorkbench.quickStyleOptions.${style
                    .toLowerCase()
                    .replace(/\s+/g, '')}`
                )}
              </button>
            ))}
          </div>
        </div>
        <div className="describe-effect">
          <h4>{t('aiWorkbench.describeEffect')}</h4>
          <textarea
            placeholder={t('aiWorkbench.describeEffectPlaceholder')}
            maxLength="5000"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
        </div>
        <div className="size-selection">
          <SizeSelector
            id="imageToImageSize"
            title={t('aiWorkbench.aspectRatio')}
            required
            value={selectedSize}
            onChange={setSelectedSize}
            disabled={isGenerating}
            props={{ defaultValue: 'Auto' }}
          />
        </div>
        <div className="generation-options">
          <span>{t('aiWorkbench.generationCount')}</span>
          <div className="options-right">
            <button className="count-btn">
              {generationCount} {t('aiWorkbench.images')}
            </button>
            <span className="credits">
              {generationCount * creditsPerImage} {t('aiWorkbench.credits')}
            </span>
          </div>
        </div>
        <button
          className={`start-btn ${isGenerating ? 'generating' : ''}`}
          onClick={handleGenerateImage}
          disabled={isGenerating || uploadedImages.length === 0 || !prompt.trim()}
          style={{ background: isGenerating ? `linear-gradient(to right, #00b894 ${generationProgress}%, #666 ${generationProgress}%)` : '#00b894' }}
        >
          {isGenerating
            ? `${t('aiWorkbench.generating')} ${Math.round(
                generationProgress
              )}% (${generationCount * creditsPerImage} ${t('credits.credits')})`
            : `${t('aiWorkbench.startGeneration')}`}
        </button>
        {generatedImage && generatedImage.url && (
          <div className="generated-image" style={{ position: 'relative' }}>
            <h4>{t('aiWorkbench.generatedImage')}</h4>
            <div style={{ position: 'relative' }}>
              <img
                src={completedImageUrl()}
                alt="Generated"
                style={{ cursor: 'zoom-in' }}
                onClick={handleImageClick}
              />
              <button
                onClick={() => {
                  if (!shareMediaUrl) return;
                  setShareOpen(true);
                }}
                disabled={!shareMediaUrl}
                style={{ position: 'absolute', top: 8, right: 8, width: 32, height: 32, borderRadius: 8, border: '1px solid #444', background: '#2a2a2a', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: shareMediaUrl ? 'pointer' : 'not-allowed', opacity: shareMediaUrl ? 1 : 0.6 }}
                aria-label={t('creationRecord.share')}
              >
                <img
                  src="/icons/share.svg"
                  alt={t('creationRecord.share')}
                  style={{ width: 16, height: 16, filter: 'brightness(0) invert(1)', padding: 0 }}
                />
              </button>
            </div>
            <div className="image-actions">
              <button className="download-btn" onClick={handleDownload}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7,10 12,15 17,10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                {t('aiWorkbench.download')}
              </button>
            </div>
          </div>
        )}
      </div>
      {showLoginModal && (
        <LoginModal
          isOpen={showLoginModal}
          onClose={handleCloseLogin}
          onLoginSuccess={handleLoginSuccess}
        />
      )}
      <ErrorModal
        open={showUploadErrorModal}
        title={t('common.warning')}
        message={`${t('imageService.unsupportedFormat')}\n${t('imageService.fileTooLarge')}`}
        onClose={() => setShowUploadErrorModal(false)}
      />
      <ShareTextModal
        open={shareOpen}
        title={t('creationRecord.enterShareText')}
        placeholder={t('creationRecord.enterShareText')}
        maxLength={2000}
        platform={null}
        mediaUrl={shareMediaUrl}
        mediaType="image"
        by=""
        onConfirm={() => { setShareOpen(false); }}
        onCancel={() => { setShareOpen(false); }}
      />
    </div>
  );
};

export default ImageEditingPanelView;
